import { DatePipe } from '@angular/common';
import { takeUntil } from 'rxjs/operators';
import { Component, ElementRef, Injector } from '@angular/core';
import { Validators } from '@angular/forms';
import {
  BaseResourceFormComponent, Convenio, ConvenioAditamento, ConvenioAlteracao,
  ConvenioComissao, ConvenioHistorico, ConvenioReajuste, ConvenioStorage,
  DateFormatPipe, GlobalService, LoginPublico, Licitacao
} from 'eddydata-lib';
import { ConvenioService } from '../service/convenio.service';
import { SelectItem } from 'primeng/api';
import { ConvenioHistoricoService } from '../service/convenio-historico.service';
import { ConvenioComissaoService } from '../convenio-comissao/service/convenio-comissao.service';
import { ConvenioStorageService } from '../service/convenio-storage.service';
import { ConvenioRecursoService } from '../convenio-recurso/service/convenio-recurso.service';
import { ConvenioAditamentoService } from '../service/convenio-aditamento.service';
import { ConvenioAlteracaoService } from '../convenio-alteracao/service/convenio-alteracao.service';
import { ConvenioSituacaoService } from '../service/convenio-situacao.service';
import { ConvenioReajusteService } from '../service/convenio-reajuste.service';

@Component({
  selector: 'lib-convenio-shw',
  templateUrl: './convenio-shw.component.html'
})
export class ConvenioShwComponent extends BaseResourceFormComponent<Convenio, LoginPublico> {

  /**
   * Declaração de variáveis
   */
  private pagina: string;
  private data1: string;
  private data2: string;
  private filtro: string;
  private datepipe: DatePipe;

  public listaHistoricos: Array<ConvenioHistorico> = new Array<any>();
  public listaAditamentos: Array<ConvenioAditamento> = new Array<any>();
  public listaComissoes: Array<ConvenioComissao> = new Array<any>();
  public listaAlteracoes: ConvenioAlteracao[] = [];
  public listaFichasDespesas: Array<any> = [];
  public listaFichasReceitas: Array<any> = [];
  public listaSituacoes: Array<any> = [];
  public listaRecursos: Array<any> = [];
  public listaStorage: ConvenioStorage[] = [];
  public listaReajustes: Array<ConvenioReajuste> = new Array<any>();
  public listaLicitacoes: Array<Licitacao> = new Array<any>();
  public itens: SelectItem[];
  public selectedItem: any;

  /**
   * Construtor com as injeções de dependencias
   */
  constructor(
    protected injector: Injector,
    protected globalService: GlobalService,
    protected convenioService: ConvenioService,
    protected historicoService: ConvenioHistoricoService,
    protected comissaoService: ConvenioComissaoService,
    protected convenioStorageService: ConvenioStorageService,
    protected convenioRecursoService: ConvenioRecursoService,
    protected aditamentoService: ConvenioAditamentoService,
    protected alteracaoService: ConvenioAlteracaoService,
    protected situacaoService: ConvenioSituacaoService,
    protected reajusteService: ConvenioReajusteService) {
    super(new Convenio(), injector, Convenio.converteJson, convenioService);
    this.datepipe = new DatePipe('pt');
    this.activatedRoute.params.pipe(takeUntil(this.unsubscribe))
      .subscribe(params => {
        this.pagina = params['pagina'];
        this.data1 = params['data1'];
        this.data2 = params['data2'];
        this.filtro = params['filtro'];
      });
  }

  // ========================================================================
  //                        MÉTODOS ABSTRAÍDOS
  // ========================================================================

  protected criarCamposForm(): void {
    this.entidadeForm = this.fb.group({
      id: [null],
      numero: [null, [Validators.required]],
      ano: [null],
      valor_convenio: 0,
      valor_contrapartida: 0,
      data_assinatura: [null],
      data_inicio: [null, [Validators.required]],
      data_termino: [null, [Validators.required]],
      data_contabilizacao: [null],
      finalidade: [null, [Validators.required]],
      favorecido: [null, [Validators.required]],
      recurso: [null],
      aplicacao: [null],
      aplicacao_variavel: [null],
      objeto: [null, [Validators.required]],
    });
  }

  protected parametrosExtras(): {} {
    return {
      relations: 'favorecido,favorecido.tipo,orgao,orgao.cidade,recurso,aplicacao,aplicacao_variavel,unidade,tipo_convenio,recurso_contra,aplicacao_contra,aplicacao_variavel_contra,exercicio'
    };
  }

  protected afterInit(): void {
    this.login.contraste = sessionStorage.getItem('contraste') === 'true';
  }

  protected campoFoco(): ElementRef {
    return null;
  }

  protected beforeSubmit(): void {
  }

  protected afterSubmit(entidade: Convenio): void {
  }

  protected afterLoad() {
    this.entidade.data_assinatura = new DateFormatPipe().transform(this.entidade.data_assinatura, []);
    this.entidade.data_inicio = new DateFormatPipe().transform(this.entidade.data_inicio, []);
    this.entidade.data_termino = new DateFormatPipe().transform(this.entidade.data_termino, []);
    this.entidade.data_contabilizacao = new DateFormatPipe().transform(this.entidade.data_contabilizacao, []);

    this.itens = [];
    this.itens.push({ label: 'EMPENHOS', value: '1' });
    this.itens.push({ label: 'LIQUIDAÇÕES', value: '2' });
    this.itens.push({ label: 'PAGAMENTOS', value: '3' });
    this.itens.push({ label: 'RECEBIMENTOS', value: '4' });
    this.selectedItem = { label: 'PAGAMENTOS', value: '3' };

    const param = { 'convenio.id': this.entidade.id };

    this.historicoService.filtrar(0, -1, param).pipe(takeUntil(this.unsubscribe)).subscribe((data: any) => {
      this.listaHistoricos = data ? data.content : [];
    });

    this.comissaoService.filtrar(0, -1, param).pipe(takeUntil(this.unsubscribe)).subscribe((data: any) => {
      this.listaComissoes = data ? data.content : [];
    });

    this.convenioStorageService.filtrar(0, -1, param).pipe(takeUntil(this.unsubscribe)).subscribe((data: any) => {
      this.listaStorage = data ? data.content : [];
    });

    this.convenioRecursoService.filtrar(0, -1, {
      'convenio.id': this.entidade.id,
      relations: 'convenio,recurso,aplicacao,aplicacao_variavel,recurso_contra,aplicacao_contra,aplicacao_variavel_contra'
    }).pipe(takeUntil(this.unsubscribe)).subscribe((data: any) => {
      this.listaRecursos = data ? data.content : [];
    });

    this.aditamentoService.filtrar(0, -1, {
      'convenio.id': this.entidade.id, relations: 'convenio,convenio.tipo_convenio'
    }).pipe(takeUntil(this.unsubscribe)).subscribe((data: any) => {
      this.listaAditamentos = data ? data.content : [];
    });

    this.alteracaoService.filtrar(0, -1, {
      'convenio.id': this.entidade.id,
      relations: 'convenio,convenio.tipo_convenio,tipo_convenio'
    }).pipe(takeUntil(this.unsubscribe)).subscribe((data: any) => {
      this.listaAlteracoes = data ? data.content : [];
    });

    this.reajusteService.filtrar(0, -1, {
      'convenio.id': this.entidade.id, relations: 'convenio'
    }).pipe(takeUntil(this.unsubscribe)).subscribe((data: any) => {
      this.listaReajustes = data ? data.content : [];
    });

    this.situacaoService.filtrar(0, -1, {
      'convenio.id': this.entidade.id,
      relations: 'itens,usuario,convenio,convenio.orgao,convenio.orgao.cidade',
      orderBy: 'id$DESC'
    }).pipe(takeUntil(this.unsubscribe)).subscribe((data: any) => {
      this.listaSituacoes = data ? data.content : [];
    });

    this.convenioService.obterLicitacaoVinculada(this.entidade.id).pipe(takeUntil(this.unsubscribe)).subscribe((data: any) => {
      this.listaLicitacoes = data ? data : [];
    });

    if (this.entidade.orgao) {
      this.convenioRecursoService.obterRelacionamentosFichas(this.entidade.id, this.entidade.orgao.id)
        .pipe(takeUntil(this.unsubscribe)).subscribe(res => {
          this.listaFichasDespesas = [];
          this.listaFichasReceitas = [];
          for (const item of res) {
            if (item.especie === 'receita') {
              this.listaFichasReceitas.push(item);
            } else {
              this.listaFichasDespesas.push(item);
            }
          }
        });
    }
  }

  public voltar() {
    const dt1 = this.datepipe.transform(this.data1, 'yyyy-MM-dd', 'GMT');
    const dt2 = this.datepipe.transform(this.data2, 'yyyy-MM-dd', 'GMT');
    if (!this.filtro || this.filtro === '') {
      this.router.navigate([`/convenios/pesquisa`, this.pagina, dt1, dt2]);
    } else {
      this.router.navigate([`/convenios/pesquisa`, this.pagina, dt1, dt2, this.filtro]);
    }
  }

}
